import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, Eye, Loader2, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { API } from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';
import { inputCls } from '../billing/shared';

const toEdit = (sections) => sections.map((s) => ({ title: s.title, text: (s.body || []).join('\n') }));
const toSave = (rows) => rows.map((r) => ({ title: r.title.trim(), body: r.text.split('\n') }));
const btn = 'inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded-lg border border-slate-300 hover:border-slate-500 disabled:opacity-50';

const SectionEditor = ({ r, i, last, set, move, remove }) => (
  <div className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`wt-section-${i}`}>
    <div className="flex gap-2">
      <input className={inputCls} value={r.title} placeholder="Section heading" onChange={(e) => set({ title: e.target.value })} data-testid={`wt-section-title-${i}`} />
      <button type="button" className={btn} disabled={i === 0} onClick={() => move(-1)} aria-label="Move up" data-testid={`wt-section-up-${i}`}><ArrowUp size={13} /></button>
      <button type="button" className={btn} disabled={last} onClick={() => move(1)} aria-label="Move down" data-testid={`wt-section-down-${i}`}><ArrowDown size={13} /></button>
      <button type="button" className={`${btn} text-red-600`} onClick={remove} aria-label="Remove section" data-testid={`wt-section-remove-${i}`}><Trash2 size={13} /></button>
    </div>
    <textarea rows={6} className={`${inputCls} font-mono text-xs`} value={r.text} onChange={(e) => set({ text: e.target.value })} data-testid={`wt-section-body-${i}`} />
  </div>
);

// Settings → e-Warranty terms: one editable set per service type.
export const WarrantyTermsSettings = () => {
  const { authHeader } = useAuth();
  const [data, setData] = useState(null);
  const [st, setSt] = useState('');
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState('');
  const load = (keep = st) => axios.get(`${API}/billing/warranty-terms`, { headers: authHeader() }).then((r) => {
    setData(r.data);
    setRows(toEdit(r.data.sets.find((s) => s.service_type === keep).sections));
  }).catch(() => toast.error('Could not load the warranty terms'));
  useEffect(() => { load(''); }, []); // eslint-disable-line
  if (!data) return <div className="p-6 text-center text-slate-500"><Loader2 className="animate-spin inline" size={16} /></div>;
  const cur = data.sets.find((s) => s.service_type === st);
  const key = st || 'default';
  const pick = (v) => { setSt(v); setRows(toEdit(data.sets.find((s) => s.service_type === v).sections)); };
  const setRow = (i, p) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...p } : r)));
  const move = (i, d) => setRows((rs) => { const n = [...rs]; [n[i], n[i + d]] = [n[i + d], n[i]]; return n; });
  const run = (what, fn) => async () => {
    setBusy(what);
    try { await fn(); } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : e?.response?.status === 422 ? 'Every section needs a heading.' : 'Something went wrong. Please try again.');
    }
    setBusy('');
  };
  const save = run('save', async () => {
    await axios.put(`${API}/billing/warranty-terms/${encodeURIComponent(key)}`, { sections: toSave(rows) }, { headers: authHeader() });
    toast.success(`Terms saved for ${cur.label}. New warranties will use them.`);
    await load(st);
  });
  const reset = run('reset', async () => {
    if (!window.confirm(st ? `Go back to the default terms for ${st}?` : 'Go back to the built-in terms for all services?')) return;
    await axios.delete(`${API}/billing/warranty-terms/${encodeURIComponent(key)}`, { headers: authHeader() });
    toast.success('Reset done');
    await load(st);
  });
  const preview = run('preview', async () => {
    const tab = window.open('', '_blank');
    const r = await axios.post(`${API}/billing/warranty-terms/preview`, { sections: toSave(rows) }, { headers: authHeader(), responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
    if (tab) tab.location.href = url; else window.location.assign(url);
  });
  return (
    <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-4" data-testid="warranty-terms-settings">
      <div>
        <h2 className="text-sm font-semibold text-teal-700 uppercase tracking-wider">e-Warranty terms &amp; conditions</h2>
        <p className="text-xs text-slate-500 mt-0.5">Printed after the certificate. Changes apply to warranties issued from now on; ones already issued keep their terms. Write <span className="font-mono">{'{company}'}</span> to print your workshop name.</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select className={`${inputCls} !w-auto`} value={st} onChange={(e) => pick(e.target.value)} data-testid="wt-service-type">
          {data.sets.map((s) => <option key={s.service_type} value={s.service_type}>{s.label}{s.custom ? ' · custom' : ''}</option>)}
        </select>
        <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${cur.custom ? 'bg-teal-50 text-teal-700' : 'bg-slate-100 text-slate-600'}`} data-testid="wt-status">
          {cur.custom ? `Custom · saved ${(cur.updated_at || '').slice(0, 10)}` : cur.uses_default ? 'Using the default terms' : 'Built-in terms'}
        </span>
      </div>
      <div className="space-y-3">{rows.map((r, i) => <SectionEditor key={i} r={r} i={i} last={i === rows.length - 1} set={(p) => setRow(i, p)} move={(d) => move(i, d)} remove={() => setRows((rs) => rs.filter((_, j) => j !== i))} />)}</div>
      <div className="flex flex-wrap gap-2">
        <button type="button" className={btn} onClick={() => setRows((rs) => [...rs, { title: '', text: '' }])} data-testid="wt-add-section"><Plus size={13} /> Add section</button>
        <button type="button" className={btn} onClick={preview} disabled={!!busy || !rows.length} data-testid="wt-preview">{busy === 'preview' ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />} Preview PDF</button>
        {cur.custom && <button type="button" className={btn} onClick={reset} disabled={!!busy} data-testid="wt-reset"><RotateCcw size={13} /> {st ? 'Use default terms' : 'Reset to built-in'}</button>}
        <button type="button" className="ml-auto inline-flex items-center gap-1.5 bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white text-sm font-semibold px-4 py-2 rounded-lg" onClick={save} disabled={!!busy || !rows.length} data-testid="wt-save">{busy === 'save' && <Loader2 size={14} className="animate-spin" />} Save terms for {st || 'all services'}</button>
      </div>
    </section>
  );
};
